import type { Card, Suit } from './cards.js';
import type { HandType } from './combos.js';
import type { RoomHistoryLink } from './history.js';
import type { DaaluuView } from './daaluu.js';

/** Client → Server event names. */
export const C2S = {
  AUTH_REGISTER: 'AUTH_REGISTER',
  AUTH_LOGIN: 'AUTH_LOGIN',
  AUTH_RESTORE: 'AUTH_RESTORE',
  AUTH_LOGOUT: 'AUTH_LOGOUT',
  AUTH_VERIFY_EMAIL: 'AUTH_VERIFY_EMAIL',
  AUTH_RESEND_VERIFICATION: 'AUTH_RESEND_VERIFICATION',
  AUTH_FORGOT_PASSWORD: 'AUTH_FORGOT_PASSWORD',
  AUTH_RESET_PASSWORD: 'AUTH_RESET_PASSWORD',
  AUTH_CHANGE_PASSWORD: 'AUTH_CHANGE_PASSWORD',
  AUTH_UPDATE_PROFILE: 'AUTH_UPDATE_PROFILE',
  AUTH_DELETE_ACCOUNT: 'AUTH_DELETE_ACCOUNT',
  AUTH_OAUTH_URL: 'AUTH_OAUTH_URL',
  CLAIM_WEEKLY_TSAI: 'CLAIM_WEEKLY_TSAI',
  INITIATE_PAYMENT: 'INITIATE_PAYMENT',
  CHECK_PAYMENT: 'CHECK_PAYMENT',
  REDEEM_COUPON: 'REDEEM_COUPON',
  BILLING_HISTORY: 'BILLING_HISTORY',
  ADMIN_LIST: 'ADMIN_LIST',
  ADMIN_MUTATE: 'ADMIN_MUTATE',
  ROOM_HISTORY: 'ROOM_HISTORY',
  CREATE_ROOM: 'CREATE_ROOM',
  JOIN_ROOM: 'JOIN_ROOM',
  REJOIN_ROOM: 'REJOIN_ROOM',
  LEAVE_ROOM: 'LEAVE_ROOM',
  ROOM_CONFIG: 'ROOM_CONFIG',
  START_GAME: 'START_GAME',
  RETURN_TO_LOBBY: 'RETURN_TO_LOBBY',
  DAALUU_ACTION: 'DAALUU_ACTION',
  DAALUU_SWEEP: 'DAALUU_SWEEP',
  DAALUU_RETRY: 'DAALUU_RETRY',
  PLAY_CARDS: 'PLAY_CARDS',
  PASS_TURN: 'PASS_TURN',
  PAUSE_ACTION: 'PAUSE_ACTION',
  SEND_REACTION: 'SEND_REACTION',
  VOICE_STATE: 'VOICE_STATE',
  VOICE_SIGNAL: 'VOICE_SIGNAL',
  LIST_PUBLIC_ROOMS: 'LIST_PUBLIC_ROOMS',
  GET_LEADERBOARD: 'GET_LEADERBOARD',
  FRIENDS_GET: 'FRIENDS_GET',
  FRIEND_REQUEST_SEND: 'FRIEND_REQUEST_SEND',
  FRIEND_REQUEST_RESPOND: 'FRIEND_REQUEST_RESPOND',
  FRIEND_REMOVE: 'FRIEND_REMOVE',
  QUICK_PLAY: 'QUICK_PLAY',
  CANCEL_MATCHMAKING: 'CANCEL_MATCHMAKING',
  PING: 'PING',
  TABLE_THROW: 'TABLE_THROW',
  VOICE_TAUNT: 'VOICE_TAUNT',
  SUBMIT_FEEDBACK: 'SUBMIT_FEEDBACK',
  GET_PLAYER_PROFILE: 'GET_PLAYER_PROFILE',
  KICK_PLAYER: 'KICK_PLAYER',
  /** Lobby only. Take a seat; the host may also move or swap another player (`playerId`). */
  CHOOSE_SEAT: 'CHOOSE_SEAT',
  /** In game. `{ enabled }`: the bot engine plays for the sender until switched off. */
  SET_AUTOPLAY: 'SET_AUTOPLAY',
} as const;

/** Server → Client event names. */
export const S2C = {
  CAPABILITIES: 'CAPABILITIES',
  AUTH_STATE: 'AUTH_STATE',
  AUTH_NOTICE: 'AUTH_NOTICE',
  AUTH_OAUTH_URL: 'AUTH_OAUTH_URL',
  ROOM_JOINED: 'ROOM_JOINED',
  LOBBY_UPDATE: 'LOBBY_UPDATE',
  PUBLIC_ROOMS: 'PUBLIC_ROOMS',
  MATCHMAKING_STATUS: 'MATCHMAKING_STATUS',
  LEADERBOARD_DATA: 'LEADERBOARD_DATA',
  FRIENDS_DATA: 'FRIENDS_DATA',
  FRIEND_REQUEST_RECEIVED: 'FRIEND_REQUEST_RECEIVED',
  PLAYER_PROFILE: 'PLAYER_PROFILE',
  GAME_START: 'GAME_START',
  MOVE_ACCEPTED: 'MOVE_ACCEPTED',
  PLAYER_PASSED: 'PLAYER_PASSED',
  TURN_CHANGED: 'TURN_CHANGED',
  TRICK_CLEARED: 'TRICK_CLEARED',
  GAME_OVER: 'GAME_OVER',
  PLAYER_REACTION: 'PLAYER_REACTION',
  TABLE_THROW: 'TABLE_THROW',
  VOICE_TAUNT: 'VOICE_TAUNT',
  VOICE_PARTICIPANTS: 'VOICE_PARTICIPANTS',
  VOICE_SIGNAL: 'VOICE_SIGNAL',
  SYNC_GAME_STATE: 'SYNC_GAME_STATE',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  ERROR: 'ERROR',
  PAYMENT_INFO: 'PAYMENT_INFO',
  COUPON_QUOTE: 'COUPON_QUOTE',
  BILLING_HISTORY: 'BILLING_HISTORY',
  ADMIN_DATA: 'ADMIN_DATA',
  ADMIN_SAVED: 'ADMIN_SAVED',
  FEEDBACK_SUBMITTED: 'FEEDBACK_SUBMITTED',
  ROOM_HISTORY: 'ROOM_HISTORY',
  PLAYER_KICKED: 'PLAYER_KICKED',
} as const;

export const FREE_REACTION_IDS = [
  'thumbs_up',
  'laugh',
  'fire',
  'coffee',
  'clap',
  'sweat',
] as const;

export const PREMIUM_REACTION_IDS = [
  'boss_deuce',
  'airag_toast',
  'dragon_fire',
  'hurry_horse',
  'shock_boom',
  'cry_river',
] as const;

export const REACTION_IDS = [...FREE_REACTION_IDS, ...PREMIUM_REACTION_IDS] as const;

export type ReactionId = (typeof REACTION_IDS)[number];

export function isPremiumReaction(id: ReactionId): boolean {
  return (PREMIUM_REACTION_IDS as readonly string[]).includes(id);
}

export const THROWABLE_IDS = [
  'tea',
  'slipper',
  'egg',
  'airag',
  'cash',
  'shagai',
] as const;

export type ThrowableId = (typeof THROWABLE_IDS)[number];

export function isThrowableId(id: unknown): id is ThrowableId {
  return typeof id === 'string' && (THROWABLE_IDS as readonly string[]).includes(id);
}

export const VOICE_TAUNT_IDS = [
  'hurry_sheep',
  'wake_up',
  'frozen',
  'powder',
  'sorry_bro',
  'got_money',
] as const;

export type VoiceTauntId = (typeof VOICE_TAUNT_IDS)[number];

export function isVoiceTauntId(id: unknown): id is VoiceTauntId {
  return typeof id === 'string' && (VOICE_TAUNT_IDS as readonly string[]).includes(id);
}

export interface TableThrowPayload {
  targetSeat: number;
  throwableId: ThrowableId;
}

export interface TableThrowBroadcast {
  fromSeat: number;
  fromPlayerId: string;
  fromPlayerName: string;
  toSeat: number;
  toPlayerId: string;
  toPlayerName: string;
  throwableId: ThrowableId;
  seq: number;
}

export interface VoiceTauntPayload {
  tauntId: VoiceTauntId;
  targetSeat?: number;
}

export interface VoiceTauntBroadcast {
  fromSeat: number;
  fromPlayerId: string;
  fromPlayerName: string;
  tauntId: VoiceTauntId;
  targetSeat?: number;
  seq: number;
}

export type SeatCount = 2 | 3 | 4 | 5;
export type GameType = 'big2' | 'muushig' | 'twentyone' | 'daaluu';
export type RoomPhase = 'lobby' | 'playing' | 'finished';
export type RoomMode = 'free' | 'tsai';
export type UserTier = 'free' | 'paid';

export const DEFAULT_BIG2_POINT_LIMIT = 50;
export const MUUSHIG_START_SCORE = 15;
export const MUUSHIG_PENALTY = 5;
export const TWENTYONE_START_CHIPS = 100;

export const INITIAL_TSAI_BALANCE = 100;
export const WEEKLY_FREE_TSAI_AMOUNT = 40;
export const WEEKLY_PAID_TSAI_AMOUNT = 160;
export const WEEKLY_TSAI_AMOUNT = WEEKLY_FREE_TSAI_AMOUNT;
export const WEEKLY_TSAI_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

export function weeklyTsaiForTier(tier: UserTier): number {
  return tier === 'paid' ? WEEKLY_PAID_TSAI_AMOUNT : WEEKLY_FREE_TSAI_AMOUNT;
}

/**
 * Minimum Цай (Tsai) balance required to join or start a round in a Token Room (`tsai` mode).
 * - Big Two: derived from max single-round penalty (`cardsEach * 3`): 39 Цай (2, 3, or 4 seats).
 * - Muushig: 15 Цай (covering the 15-point starting score / +5 Muushig penalty).
 * - 21: 20 Цай (covering minimum bank pot / table bets).
 */
export function minTsaiForSeats(_seats: SeatCount, gameType: GameType = 'big2'): number {
  if (gameType === 'daaluu') return 0; // Room-local tea; account Tsai rooms are not supported.
  if (gameType === 'muushig') return 15;
  if (gameType === 'twentyone') return 20;
  return 39;
}

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  emailVerified?: boolean;
  createdAt?: string;
  tier: UserTier;
  subscriptionExpiresAt?: string | null;
  isAdmin?: boolean;
  tsaiBalance: number;
  weeklyRewardAmount: number;
  lastWeeklyClaimAt: string;
  nextWeeklyClaimAt: string;
  weeklyClaimAvailable: boolean;
  wins: number;
  gamesPlayed: number;
  xp: number;
  level: number;
}

export type GameSpeed = 'relaxed' | 'normal' | 'fast';
export type BotDifficulty = 'easy' | 'medium' | 'hard';

export interface RoomConfig {
  seats: SeatCount;
  fillWithBots: boolean;
  mode?: RoomMode;
  gameType?: GameType;
  gameSpeed?: GameSpeed;
  botDifficulty?: BotDifficulty;
  voiceChatEnabled?: boolean;
  isPublic?: boolean;
  pointLimit?: number | null;
  /** Daaluu only: replies must follow the led color. Missing = true. */
  daaluuFollowColor?: boolean;
}

export interface LobbyPlayer {
  playerId: string;
  name: string;
  isBot: boolean;
  isHost: boolean;
  connected: boolean;
  isRegistered?: boolean;
  tsaiBalance?: number | null;
  tier?: UserTier;
  level?: number;
  userId?: string;
  botControlled?: boolean;
  abandoned?: boolean;
  /** Zero-based table seat. In the lobby this is the chosen seat; empty seats are filled by bots at start. */
  seat?: number;
}

export interface ChooseSeatPayload {
  seat: number;
  /** Host only: the player to move. Omit to move yourself. */
  playerId?: string;
}

/** Public, zero-knowledge information about a seated player (NFR-1.01). */
export interface PublicPlayer {
  playerId: string;
  name: string;
  seat: number;
  isBot: boolean;
  /** Human seat currently driven by the bot engine after the reconnect grace expired. */
  botControlled: boolean;
  /** Competitive Tsai mode seat that abandoned the match (no bot replacement). */
  abandoned?: boolean;
  /** Connected player who switched on auto-play. */
  autoPlay?: boolean;
  connected: boolean;
  cardCount: number;
  userId?: string;
  /** Muushig: number of tricks won in the current round. */
  tricksWon?: number;
  /** Muushig: whether the player resigned/passed before the round began. */
  folded?: boolean;
  /** Muushig: whether the player has completed their card replacement decision. */
  exchanged?: boolean;
  /** Muushig: whether the player has decided whether to stay or sit out. */
  decided?: boolean;
  tier?: UserTier;
  level?: number;
}

export type SeatAction = { type: 'play'; cards: Card[] } | { type: 'pass' };

export interface TrickView {
  playerId: string;
  cards: Card[];
  handType: HandType;
}

export interface GameFormat {
  seats: SeatCount;
  cardsEach: number;
  passesToClear: number;
  removed: Card[];
}

export interface MuushigTrickCard {
  playerId: string;
  seat: number;
  card: Card;
}

export interface MuushigView {
  stage: 'decision' | 'exchange' | 'trump-exchange' | 'playing';
  trumpCard: Card;
  trumpSuit: Suit;
  stockCount: number;
  trickCards: MuushigTrickCard[];
  leadSuit: Suit | null;
  lastTrick: {
    winnerPlayerId: string;
    cards: MuushigTrickCard[];
  } | null;
  tricksWon: Record<string, number>;
  foldedPlayerIds: string[];
  exchangedPlayerIds: string[];
  decidedPlayerIds?: string[];
  scoreDeltas?: Record<string, number> | null;
  dealerSeat: number;
  scores: Record<string, number>;
}

export type PauseActionType = 'request' | 'agree_pause' | 'decline_pause' | 'agree_resume';

export interface PauseView {
  status: 'requesting' | 'paused';
  requestedByPlayerId: string;
  requestedByName: string;
  pauseAgreedPlayerIds: string[];
  resumeAgreedPlayerIds: string[];
  requiredPlayerIds: string[];
}

export interface TwentyOneView {
  stage: 'betting' | 'playing' | 'banker' | 'finished';
  bankerSeat: number;
  bankPot: number;
  bets: Record<string, number>;
  bustedPlayerIds: string[];
  stoodPlayerIds: string[];
  golden21PlayerIds: string[];
  scores: Record<string, number>;
  myScore: number;
  bankerHand?: Card[];
  bankerScore?: number;
  roundNetTransfers?: Record<string, number>;
}

export interface GameView {
  roomId: string;
  gameType?: GameType;
  phase: 'playing' | 'finished';
  format: GameFormat;
  myPlayerId: string;
  mySeat: number;
  myHand: Card[];
  players: PublicPlayer[];
  activePlayerId: string | null;
  timeoutEndsAt: number | null;
  turnDurationMs?: number;
  botDifficulty?: BotDifficulty;
  gameSpeed?: GameSpeed;
  pause?: PauseView | null;
  trick: TrickView | null;
  lastActions: Record<string, SeatAction>;
  mustInclude: Card | null;
  winnerId: string | null;
  penalties: Record<string, number> | null;
  totals: Record<string, number>;
  pointLimit?: number | null;
  muushig?: MuushigView;
  twentyone?: TwentyOneView;
  daaluu?: DaaluuView;
  serverTime?: number;
}

// ---- Payloads -------------------------------------------------------------

export interface AuthRegisterPayload {
  email: string;
  password: string;
  displayName: string;
}
export interface AuthLoginPayload {
  email: string;
  password: string;
}
export interface AuthRestorePayload {
  authToken: string;
  refreshToken?: string;
}
export type AuthOtpType = 'signup' | 'email' | 'recovery' | 'email_change';
export interface AuthVerifyEmailPayload {
  email?: string;
  token?: string;
  tokenHash?: string;
  type?: AuthOtpType;
}
export interface AuthResendVerificationPayload {
  email: string;
}
export interface AuthForgotPasswordPayload {
  email: string;
}
export interface AuthResetPasswordPayload {
  email?: string;
  token?: string;
  recoveryToken?: string;
  newPassword: string;
}
export interface AuthChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}
export interface AuthUpdateProfilePayload {
  displayName?: string;
  email?: string;
}
export interface AuthDeleteAccountPayload {
  currentPassword: string;
}
export interface AuthStatePayload {
  profile: UserProfile | null;
  authToken?: string | null;
  refreshToken?: string | null;
  weeklyBonusGranted?: number;
  verificationRequired?: boolean;
  pendingEmail?: string | null;
}
export type AuthNoticeKind =
  | 'verification_sent'
  | 'email_verified'
  | 'password_reset_sent'
  | 'password_reset_ready'
  | 'password_updated'
  | 'profile_updated'
  | 'email_change_sent'
  | 'account_deleted'
  | 'payment_success'
  | 'coupon_redeemed';
export interface AuthNoticePayload {
  kind: AuthNoticeKind;
  message: string;
  email?: string;
  recoveryToken?: string;
}

export type OAuthProvider = 'google';

export interface AuthOAuthUrlPayload {
  provider: OAuthProvider;
}

export interface AuthOAuthUrlResponse {
  provider: OAuthProvider;
  url: string;
}

export interface InitiatePaymentPayload {
  tier?: UserTier;
  couponCode?: string;
}
export interface CheckPaymentPayload {
  invoiceId: string;
  silent?: boolean;
}
export interface RedeemCouponPayload {
  code: string;
}
export interface PaymentInfoPayload {
  invoiceId: string;
  qrText: string;
  qrImage?: string | null;
  urls: { name: string; link: string; logo?: string }[];
  amount?: number;
  itemDescription?: string;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  tier: UserTier;
  wins: number;
  gamesPlayed: number;
  tsaiBalance: number;
  xp?: number;
  level?: number;
}
export interface LeaderboardPayload {
  entries: LeaderboardEntry[];
  myRank?: number;
}

export interface FriendUser {
  userId: string;
  displayName: string;
  tier: UserTier;
  level: number;
  xp: number;
  online: boolean;
  currentRoomId?: string;
}

export interface FriendRequest {
  id: string;
  fromUserId: string;
  fromDisplayName: string;
  fromLevel: number;
  toUserId: string;
  toDisplayName: string;
  createdAt: string;
}

export interface FriendsDataPayload {
  friends: FriendUser[];
  incomingRequests: FriendRequest[];
  outgoingRequests: FriendRequest[];
}

export interface SendFriendRequestPayload {
  toUserId?: string;
  displayNameOrEmail?: string;
}

export interface RespondFriendRequestPayload {
  requestId: string;
  accept: boolean;
}

export interface RemoveFriendPayload {
  friendUserId: string;
}

export interface GetPlayerProfilePayload {
  targetUserId: string;
}

export interface QuickPlayPayload {
  playerName: string;
  gameType?: GameType;
  mode?: RoomMode;
  /** When true, places the player in the server matchmaking queue (up to 2 min) instead of immediately creating a solo room. */
  queue?: boolean;
}

export interface MatchmakingStatusPayload {
  status: 'searching' | 'found' | 'timeout' | 'cancelled';
  gameType: GameType;
  mode: RoomMode;
  elapsedSeconds: number;
  estimatedSeconds: number;
  timeoutSeconds: number;
  playersInQueue: number;
  openRoomsCount: number;
  roomId?: string;
}

export interface CreateRoomPayload {
  playerName: string;
  mode?: RoomMode;
  customRoomName?: string;
  gameType?: GameType;
  voiceChatEnabled?: boolean;
  isPublic?: boolean;
}
export interface PublicRoomSummary {
  roomId: string;
  roomName: string | null;
  hostName: string;
  hostTier: UserTier;
  gameType: GameType;
  mode: RoomMode;
  seats: SeatCount;
  playerCount: number;
  playersNeeded: number;
  voiceChatEnabled: boolean;
  minTsai?: number;
}
export interface PublicRoomsPayload {
  rooms: PublicRoomSummary[];
}
export interface JoinRoomPayload {
  roomId: string;
  playerName: string;
  sessionId?: string;
}
export interface KickPlayerPayload {
  playerId: string;
}
export interface PlayerKickedPayload {
  reason?: string;
  cooldownSeconds?: number;
}
export type RoomConfigPayload = RoomConfig;
export interface PlayCardsPayload {
  cards: Card[];
}
export interface PauseActionPayload {
  action: PauseActionType;
}
export interface SendReactionPayload {
  reactionId: ReactionId;
}

export interface VoiceParticipant {
  playerId: string;
  name: string;
  muted: boolean;
}
export interface VoiceStatePayload {
  joined: boolean;
  muted: boolean;
}
export interface VoiceSdpPayload {
  type: 'offer' | 'answer';
  sdp: string;
}
export interface VoiceIceCandidatePayload {
  candidate: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
  usernameFragment?: string | null;
}
export interface VoiceSignalPayload {
  targetPlayerId: string;
  type: 'offer' | 'answer' | 'ice';
  sdp?: VoiceSdpPayload;
  candidate?: VoiceIceCandidatePayload;
}
export interface VoiceParticipantsPayload {
  roomId: string;
  voiceEnabled: boolean;
  participants: VoiceParticipant[];
}
export interface VoiceSignalRelayPayload {
  fromPlayerId: string;
  type: 'offer' | 'answer' | 'ice';
  sdp?: VoiceSdpPayload;
  candidate?: VoiceIceCandidatePayload;
}

export interface RoomJoinedPayload {
  roomId: string;
  playerId: string;
  sessionId: string;
  history?: RoomHistoryLink;
  gameType?: GameType;
  mode?: RoomMode;
}
export interface LobbyUpdatePayload {
  supportedGames?: GameType[];
  roomId: string;
  roomName?: string | null;
  hostId: string;
  phase: RoomPhase;
  players: LobbyPlayer[];
  config: RoomConfig;
  totals: Record<string, number>;
  minTsai?: number;
  lastTsaiTransfers?: Record<string, number> | null;
}
export interface GameStartPayload {
  myHand: Card[];
  players: PublicPlayer[];
  activeTurnId: string;
  gameType?: GameType;
  mode?: RoomMode;
  round?: number;
}
export interface MoveAcceptedPayload {
  playerId: string;
  cards: Card[];
  handType: HandType;
  remainingCards: number;
}
export interface PlayerPassedPayload {
  playerId: string;
}
export interface TurnChangedPayload {
  activePlayerId: string;
  timeoutEndsAt: number;
}
export interface TrickClearedPayload {
  nextLeadPlayerId: string;
}
export interface GameOverPayload {
  winnerId: string;
  penalties: Record<string, number>;
  remaining: Record<string, number>;
  totals: Record<string, number>;
  tsaiTransfers?: Record<string, number>;
  scoreDeltas?: Record<string, number>;
  tricksWon?: Record<string, number>;
  foldedPlayerIds?: string[];
  gameType?: GameType;
  matchOver?: boolean;
  matchWinnerId?: string;
  matchRankings?: { playerId: string; rank: number; score: number }[];
  xpGained?: Record<string, number>;
}
export interface PlayerReactionPayload {
  playerId: string;
  playerName: string;
  reactionId: ReactionId;
}
export interface ErrorPayload {
  message: string;
}

export const MAX_NAME_LENGTH = 20;
export const MAX_ROOM_NAME_LENGTH = 20;
export const ROOM_CODE_LENGTH = 5;
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
